"use client";

import { marketingActions } from "@/modules/landing/content/marketingContent";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { FormHoneypot } from "@/shared/components/FormHoneypot";
import { coreServices } from "@/shared/config/coreServices";
import { createEnquiry } from "@/shared/api/enquiries";
import { getApiErrorMessage } from "@/shared/api/client";
import { buildEnquiryPayload, validateEnquiry, type EnquiryField as FieldName, type EnquiryValues } from "@/shared/lib/enquiryValidation";
import { usePublicFormGuard } from "@/shared/hooks/usePublicFormGuard";
import { fetchEnquiryPhoneCountries, type EnquiryPhoneCountry } from "@/shared/services/enquiryPhoneCountries";
import styles from "./InquiryModal.module.scss";

function readValues(form: HTMLFormElement): EnquiryValues {
  const data = new FormData(form);
  return Object.fromEntries(
    ["name", "phoneCountry", "phone", "email", "location", "service", "message"].map((name) => [name, String(data.get(name) ?? "")]),
  ) as EnquiryValues;
}

export function InquiryModal({ onClose, open }: { onClose: () => void; open: boolean }) {
  // Mount a fresh form on each opening while the shared browser attempt history persists.
  return open ? <InquiryDialog onClose={onClose} /> : null;
}

function InquiryDialog({ onClose }: { onClose: () => void }) {
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [countries, setCountries] = useState<EnquiryPhoneCountry[]>([]);
  const [phoneCountry, setPhoneCountry] = useState("");
  const [countryError, setCountryError] = useState("");
  const [countryRetry, setCountryRetry] = useState(0);
  const dialogRef = useRef<HTMLElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const guard = usePublicFormGuard("inquiry");
  const id = useId();

  useEffect(() => {
    const controller = new AbortController();
    setCountryError("");
    fetchEnquiryPhoneCountries(controller.signal).then((options) => {
      if (controller.signal.aborted) return;
      setCountries(options);
      setPhoneCountry(options.find((country) => country.code === "IN")?.code ?? options[0].code);
    }).catch(() => {
      if (!controller.signal.aborted) setCountryError("Could not load country codes. Please retry to enter your phone number.");
    });
    return () => controller.abort();
  }, [countryRetry]);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (sent) confirmationRef.current?.focus();
  }, [sent]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLInputElement>('input[name="name"]')?.focus());

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]',
      )).filter((element) => element.tabIndex >= 0 && !element.closest('[aria-hidden="true"]') && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sent || guard.isSubmitting || !countries.length) return;
    setSubmitError("");
    if (guard.honeypotRef.current?.value) return;
    const values = readValues(event.currentTarget);
    const nextErrors = validateEnquiry(values);
    setErrors(nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      event.currentTarget.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus();
      return;
    }
    if (!guard.beginAttempt()) return;
    try {
      await createEnquiry(buildEnquiryPayload(values));
      setSent(true);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error));
    } finally {
      guard.endAttempt();
    }
  }

  const fieldProps = (name: FieldName) => ({
    disabled: guard.isSubmitting || sent,
    onChange: (event: FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const form = event.currentTarget.form;
      if (!form) return;
      const nextErrors = validateEnquiry(readValues(form));
      if (name === "phoneCountry") setPhoneCountry(event.currentTarget.value);
      setErrors((current) => ({
        ...current, [name]: nextErrors[name],
        ...(name === "phoneCountry" ? { phone: readValues(form).phone ? nextErrors.phone : undefined } : {}),
      }));
      setSubmitError("");
    },
    onBlur: (event: FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const form = event.currentTarget.form;
      if (form) setErrors((current) => ({ ...current, [name]: validateEnquiry(readValues(form))[name] }));
    },
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${id}-${name}-error` : undefined,
  });
  const fieldError = (name: FieldName) => errors[name]
    ? <span className={styles.error} id={`${id}-${name}-error`} aria-live="polite">{errors[name]}</span>
    : null;

  return (
    <div className="wireframe-inquiry-backdrop" role="presentation" onMouseDown={onClose}>
      <section aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} aria-modal="true" className="wireframe-inquiry-modal" ref={dialogRef} role="dialog" onMouseDown={(event) => event.stopPropagation()}>
        <button aria-label="Close inquiry form" className="wireframe-inquiry-close" type="button" onClick={onClose}>×</button>
        <h2 id={`${id}-title`}>{marketingActions.bookVisit}</h2>
        <p id={`${id}-description`}>Share the basic care requirement and preferred contact details. The Carevinco team can coordinate the next step.</p>
        <form className="wireframe-inquiry-form" noValidate onSubmit={handleSubmit}>
          <FormHoneypot inputRef={guard.honeypotRef} />
          <span className={styles.status}>Fields marked * are required.</span>
          <input {...fieldProps("name")} aria-label="Your name" autoComplete="name" maxLength={150} name="name" placeholder="Your name *" required />
          {fieldError("name")}
          <div className={styles.phoneRow}>
            <div className={styles.countryControl}>
            <select {...fieldProps("phoneCountry")} className={styles.countrySelect} aria-label="Phone country code" autoComplete="tel-country-code" disabled={!countries.length || guard.isSubmitting || sent} name="phoneCountry" required value={phoneCountry}>
              {!countries.length ? <option value="">{countryError ? "Unavailable" : "Loading…"}</option> : null}
              {countries.map((country) => <option key={country.code} value={country.code}>{country.code} {country.dialCode} — {country.name}</option>)}
            </select>
            <span className={styles.countryPrefix} aria-hidden="true">{countries.length ? `${phoneCountry} ${countries.find((country) => country.code === phoneCountry)?.dialCode ?? ""}` : countryError ? "Unavailable" : "Loading…"}</span>
            </div>
            <input {...fieldProps("phone")} aria-describedby={[`${id}-phone-hint`, errors.phone ? `${id}-phone-error` : ""].filter(Boolean).join(" ")} aria-label="Phone number" autoComplete="tel-national" disabled={!countries.length || guard.isSubmitting || sent} inputMode="tel" maxLength={30} name="phone" placeholder="Phone number *" required type="tel" />
          </div>
          <span className={styles.status} id={`${id}-phone-hint`}>Enter the national number only, without the country code.</span>
          {countryError ? <div className={styles.error} role="alert">{countryError} <button className={styles.retry} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => setCountryRetry((current) => current + 1)}>Retry</button></div> : null}
          {fieldError("phoneCountry")}
          {fieldError("phone")}
          <input {...fieldProps("email")} aria-label="Email (optional)" autoComplete="email" maxLength={254} name="email" placeholder="Email (optional)" type="email" />
          {fieldError("email")}
          <input {...fieldProps("location")} aria-label="Care location" autoComplete="address-level2" maxLength={500} name="location" placeholder="Care location *" required />
          {fieldError("location")}
          <select {...fieldProps("service")} aria-label="Service type" defaultValue="" name="service" required>
            <option disabled value="">Select service type *</option>
            {coreServices.map((service) => <option key={service.slug} value={service.title}>{service.title}</option>)}
          </select>
          {fieldError("service")}
          <textarea {...fieldProps("message")} aria-label="Care requirement, date, time and patient notes" maxLength={5000} name="message" placeholder="Care requirement, date, time and patient notes (optional)" />
          {fieldError("message")}
          {submitError ? <div className={styles.error} role="alert">{submitError}</div> : null}
          {guard.error ? <div className={styles.error} role="alert">{guard.error}</div> : null}
          {sent ? (
            <div className="wireframe-success" ref={confirmationRef} role="status" tabIndex={-1}>
              Thank you. Your inquiry has been submitted successfully.
            </div>
          ) : null}
          <div className={styles.status} aria-live="off" role="timer">
            {guard.retryAfterSeconds > 0 ? `Please wait ${guard.retryAfterSeconds} seconds before trying again.` : null}
          </div>
          <button className={`btn btn--primary ${styles.submit}`} disabled={!countries.length || sent || guard.isSubmitting || guard.retryAfterSeconds > 0} type="submit">{guard.isSubmitting ? "Submitting..." : sent ? "Inquiry Submitted" : "Submit Inquiry"}</button>
        </form>
      </section>
    </div>
  );
}
